<template>
	<div>
		<ModalBase
			container-class="rounded-lg text-white bg-black"
			ref="imageModal">
			<div
				class="h-16 max-lg:landscape:h-10 border-b border-gray-800 flex justify-between px-8 text-white items-center">
				<p class="font-thin hidden-mobile-block w-32">{{ $t("productImagesTitle") }}</p>
				<p class="text-lg w-32">{{ indexDisplay() }}</p>
				<div class="flex gap-5 w-32 justify-end">
					<IconsFontAwesome
						icon="fa-solid fa-expand"
						class="hover:cursor-pointer"
						@click="expandImage" />
					<IconsFontAwesome
						icon="fa-solid fa-download"
						class="hover:cursor-pointer"
						@click="downloadImage" />
					<IconsFontAwesome
						icon="fa-solid fa-xmark"
						class="hover:cursor-pointer"
						@click="closeModal" />
				</div>
			</div>

			<div class="flex justify-between items-center py-8 md:py-5">
				<div
					class="hoverEffects hover:bg-gray-400 hover:bg-opacity-50 hover:cursor-pointer text-white p-4 md:p-8"
					@click="prevImage">
					<IconsFontAwesome icon="fa-solid fa-arrow-left" />
				</div>

				<div class="mainImageSize md:px-8 flex justify-center items-center">
					<img
						loading="lazy"
						class="max-h-full max-w-full object-contain"
						:src="mainImageSrc" />
				</div>

				<div
					class="hoverEffects hover:bg-gray-400 hover:bg-opacity-50 hover:cursor-pointer text-white p-4 md:p-8"
					@click="nextImage">
					<IconsFontAwesome icon="fa-solid fa-arrow-right" />
				</div>
			</div>

			<div class="flex gap-8 justify-center px-10 pb-8 md:pb-5 landscape:pb-5">
				<img
					v-for="(image, index) in otherImages"
					class="w-6 h-6 md:w-16 md:h-16 lg:w-24 lg:h-24 hover:cursor-pointer hoverEffects hover:outline hover:opacity-70 outline-secondary outline-2 outline-offset-8 landscape:outline-offset-4 object-cover"
					:class="{ hidden: image.hidden, activeImage: isActiveImage(index) }"
					:src="image.src"
					@click="changeImage(index)" />
			</div>
		</ModalBase>

		<ProductFullImagePopup
			ref="expandImageModal"
			:image-src="mainImageSrc" />
	</div>
</template>

<script setup>
	//TODO add for mobile support: https://github.surmon.me/vue-awesome-swiper

	const { imageList } = defineProps({
		ticketName: String,
		imageList: Array,
	})

	const imageModal = ref(null)
	const expandImageModal = ref(null)
	const currentImageIndex = ref(0)
	const mainImageSrc = ref(imageList[currentImageIndex.value])
	const maxOtherImages = 5
	const convertOtherImagesToSlider = () => {
		//Utilize css hidden class to prevent the client from reloading images each time an image is changed
		let helperImageList = []
		const numImages = imageList.length
		if (numImages > maxOtherImages) {
			for (let i = 0; i < numImages; i++) {
				helperImageList.push({
					src: imageList[i],
					hidden: i < maxOtherImages,
				})
			}
		} else {
			helperImageList = imageList.map((imageSrc) => {
				return {
					src: imageSrc,
					hidden: false,
				}
			})
		}
		return helperImageList
	}
	const otherImages = ref(convertOtherImagesToSlider())

	const openModal = () => imageModal.value.openModal()

	const closeModal = () => imageModal.value.closeModal()

	const expandImage = () => expandImageModal.value.openModal()

	const prevImage = () => {
		let newIndex = currentImageIndex.value
		newIndex--
		if (newIndex < 0) {
			newIndex = imageList.length - 1
		}
		changeImage(newIndex)
		updateOtherImages()
	}

	const nextImage = () => {
		let newIndex = currentImageIndex.value
		newIndex++
		if (newIndex >= imageList.length) {
			newIndex = 0
		}
		changeImage(newIndex)
		updateOtherImages()
	}

	const changeImage = (index) => {
		currentImageIndex.value = index
		mainImageSrc.value = imageList[index]
		updateOtherImages()
	}

	const updateOtherImages = () => {
		//Calculate which other images should be hidden or shown
		const imageListLength = imageList.length
		if (imageListLength > maxOtherImages) {
			const subarrayStart = (currentImageIndex.value - 2 + imageListLength) % imageListLength // Calculate starting index with offset

			otherImages.value.forEach((image) => (image.hidden = true))
			for (let i = 0; i < maxOtherImages; i++) {
				const currentIndex = (subarrayStart + i) % imageListLength // Calculate current index with modulo operator
				otherImages.value[currentIndex].hidden = false
			}
		}
	}
	if (imageList.length > maxOtherImages) {
		updateOtherImages()
	}

	const isActiveImage = (index) => currentImageIndex.value === index

	const indexDisplay = () => {
		let currentIndex = currentImageIndex.value + 1
		currentIndex = `${currentIndex}`.padStart(2, "0")
		let totalIndex = imageList.length
		totalIndex = `${totalIndex}`.padStart(2, "0")
		return `${currentIndex}/${totalIndex}`
	}

	const downloadImage = async () => {
		try {
			const response = await fetch(mainImageSrc.value)
			const blob = await response.blob()
			const url = URL.createObjectURL(blob)

			const link = document.createElement("a")
			link.href = url
			//Extract the filename from the url
			link.download = mainImageSrc.value.substring(mainImageSrc.value.lastIndexOf("/") + 1)
			link.click()

			URL.revokeObjectURL(url)
		} catch (error) {
			console.error("Error downloading image:", error)
		}
	}

	onMounted(() => {
		window.addEventListener("keydown", (e) => {
			if (imageModal.value && imageModal.value.isModalOpen() && e.key === "ArrowRight") {
				nextImage()
			} else if (imageModal.value && imageModal.value.isModalOpen() && e.key === "ArrowLeft") {
				prevImage()
			}
		})
	})

	defineExpose({
		openModal,
	})
</script>

<style lang="scss" scoped>
	.mainImageSize {
		height: 50vw;
		width: 80vw;

		@media (min-width: 768px) {
			height: 40vw;
			width: 60vw;
		}

		@media (min-width: 1024px) {
			height: 30vw;
			width: 50vw;
		}

		@media (min-width: 1280px) {
			height: 30vw;
			width: 50vw;
		}

		@media (orientation: landscape) {
			height: 30vw;
			width: 40vw;

			@media (max-width: 1024px) {
				height: 22vw;
				width: 40vw;
			}
		}
	}

	.activeImage {
		@apply outline;
	}
</style>
